import { Settings, Heart, Home, FileDown, KeyRound, ChevronRight, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useSettingsStore } from '../stores/useSettingsStore';
import Toggle from '../components/common/Toggle';
import { useTranslation } from '../i18n';
import { useProductCatalog } from '../hooks/useProductCatalog';
import { useOfflineStatus } from '../hooks/useOfflineStatus';
import { useToast } from '../context/ToastContext';

const SettingsPage = () => {
  const {
    multiCartEnabled,
    setMultiCartEnabled,
    favoriteGroupsEnabled,
    setFavoriteGroupsEnabled,
    showFavoritesOnHome,
    setShowFavoritesOnHome,
  } = useSettingsStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const catalog = useProductCatalog();
  const { isOnline } = useOfflineStatus();
  const toast = useToast();

  const lastLoadedLabel = catalog.dataUpdatedAt
    ? new Date(catalog.dataUpdatedAt).toLocaleString()
    : t.settings.productData.never;

  const reloadProducts = async () => {
    const result = await catalog.refetch();
    if (result.status === 'success') {
      toast.show(t.settings.productData.reloadSuccess, 'success');
    } else {
      toast.show(t.settings.productData.reloadError, 'error');
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3">
          <Settings className="w-7 h-7 text-vivatis-600 dark:text-vivatis-400" />
          <h1 className="text-3xl font-bold text-anthracite-500 dark:text-gray-100">
            {t.settings.title}
          </h1>
        </div>
        <p className="mt-1 text-gray-500 dark:text-gray-400 ml-10">
          {t.settings.subtitle}
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
        {/* Show Favorites on Home */}
        <div className="px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-950/30 flex items-center justify-center shrink-0 mt-0.5">
                <Home className="w-5 h-5 text-amber-500 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t.settings.favoritesOnHome.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {t.settings.favoritesOnHome.description}
                </p>
              </div>
            </div>
            <div className="shrink-0 pt-1">
              <Toggle enabled={showFavoritesOnHome} onChange={setShowFavoritesOnHome} />
            </div>
          </div>
        </div>

        {/* Multi Document */}
        <div className="px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-vivatis-50 dark:bg-vivatis-950/30 flex items-center justify-center shrink-0 mt-0.5">
                <FileDown className="w-5 h-5 text-vivatis-500 dark:text-vivatis-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t.settings.multiCart.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {t.settings.multiCart.description}
                </p>
              </div>
            </div>
            <div className="shrink-0 pt-1">
              <Toggle enabled={multiCartEnabled} onChange={setMultiCartEnabled} />
            </div>
          </div>
        </div>

        {/* Favorite Groups */}
        <div className="px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-50 dark:bg-red-950/30 flex items-center justify-center shrink-0 mt-0.5">
                <Heart className="w-5 h-5 text-red-500 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t.settings.favoriteGroups.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {t.settings.favoriteGroups.description}
                </p>
              </div>
            </div>
            <div className="shrink-0 pt-1">
              <Toggle enabled={favoriteGroupsEnabled} onChange={setFavoriteGroupsEnabled} />
            </div>
          </div>
        </div>

        {/* Product Data */}
        <div className="px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center shrink-0 mt-0.5">
                <RefreshCw className="w-5 h-5 text-blue-500 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t.settings.productData.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {t.settings.productData.description}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  {t.settings.productData.lastLoaded}:{' '}
                  <span className="font-medium text-gray-700 dark:text-gray-300">{lastLoadedLabel}</span>
                </p>
              </div>
            </div>
            <div className="shrink-0 pt-1">
              <button
                type="button"
                onClick={reloadProducts}
                disabled={catalog.isFetching || !isOnline}
                title={!isOnline ? t.common.offlineTooltip : undefined}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-vivatis-600 text-white hover:bg-vivatis-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <RefreshCw className={`w-4 h-4 ${catalog.isFetching ? 'animate-spin' : ''}`} />
                {catalog.isFetching
                  ? t.settings.productData.reloading
                  : t.settings.productData.reload}
              </button>
            </div>
          </div>
        </div>

        {/* Change Password */}
        <button
          type="button"
          onClick={() => navigate('/change-password')}
          className="w-full px-6 py-5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors cursor-pointer"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-vivatis-50 dark:bg-vivatis-950/30 flex items-center justify-center shrink-0 mt-0.5">
                <KeyRound className="w-5 h-5 text-vivatis-500 dark:text-vivatis-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t.settings.changePassword.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {t.settings.changePassword.description}
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500 shrink-0" />
          </div>
        </button>
      </div>

      <VersionInfo />
    </div>
  );
};

function VersionInfo() {
  const built = new Date(__APP_VERSION__.builtAt);
  const builtLabel = Number.isNaN(built.getTime()) ? __APP_VERSION__.builtAt : built.toLocaleString();

  return (
    <p className="text-center text-xs text-gray-400 dark:text-gray-500 font-mono">
      Version {__APP_VERSION__.sha}
      <span className="text-gray-300 dark:text-gray-600"> · {__APP_VERSION__.mode} · built {builtLabel}</span>
    </p>
  );
}

export default SettingsPage;
